import { useEffect, useState, type ReactNode } from "react";
import { Btn } from "./ui/Button";
import { Card } from "./ui/Card";
import { Input } from "./ui/Input";

interface ConfirmOptions {
  title: string;
  message: ReactNode;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: string | null) => void;
}

export function useConfirmDialog() {
  const [state, setState] = useState<ConfirmState | null>(null);
  const [reason, setReason] = useState("");

  const confirm = (options: ConfirmOptions) =>
    new Promise<string | null>((resolve) => {
      setReason("");
      setState({ ...options, resolve });
    });

  const close = (value: string | null) => {
    state?.resolve(value);
    setState(null);
  };

  useEffect(() => {
    if (!state) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const dialog = state && (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(12,14,10,0.65)] z-[1000]"
      role="presentation"
      onClick={() => close(null)}
    >
      <Card
        className="w-full max-w-sm"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="m-0 mb-2.5 text-base">{state.title}</h3>
        <p className="m-0 mb-4 text-rs-light-gray text-[13px]">{state.message}</p>
        {state.reasonLabel && (
          <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray mb-[18px]">
            {state.reasonLabel}
            <Input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && close(reason)}
              placeholder={state.reasonPlaceholder}
            />
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => close(null)}>
            {state.cancelLabel ?? "Cancel"}
          </Btn>
          <Btn variant="danger" onClick={() => close(state.reasonLabel ? reason : "")}>
            {state.confirmLabel ?? "Delete"}
          </Btn>
        </div>
      </Card>
    </div>
  );

  return { confirm, dialog };
}
